<template>
    <div class="flex space-x-2">
        <button @click="preOpenModal" :class="!homepageContent && 'border border-slate-600 rounded-xl px-3.5 py-3 transition hover:bg-gray-200'">
            <QrIcon :icon="homepageContent ? 'pen' : 'images'" class="w-5"/>
        </button>

        <DeleteHomePageContent v-if="homepageContent" class="ml-auto" :content="homepageContent"/>
    </div>

    <QrModal v-model="modalOpen" :title="trans('homepage.gallery')" large @on-close="reset">
        <QrForm @submit.prevent="update" class="space-y-4">
            <QrInput
                v-model="menuButtonName"
                :label="trans('homepage.menu_button_name')" trans="homepage.menu_button_name"
                :errors="validationErrors.menu_button_name"
            />

            <div v-if="homepageContent.files.length">
                <QrParagraph :text="trans('label.images')" trans="label.images"/>
                <div class="rounded-lg bg-slate-200 p-4">
                    <Draggable
                        v-model="homepageContent.files"
                        class="grid grid-cols-4 md:grid-cols-5 gap-2"
                        group="files"
                        @start="isDragging = true"
                        @end="isDragging = false"
                        item-key="id"
                    >
                        <template #item="{element: file}">
                            <Thumbnail
                                :path="file.file"
                                class="group relative transition"
                                :classes="[
                                    isDragging && 'image-shake',
                                    'w-full h-20 object-cover rounded-lg hover:cursor-move'
                                ]"
                                :style="{
                                    'animation-duration': getRandomNumber(0.3, 0.2),
                                    'animation-delay': getRandomNumber(-0.8, -0.2),
                                }"
                            >
                                <button
                                    type="button"
                                    :disabled="loading"
                                    class="opacity-0 transition h-6 w-6 flex group-hover:opacity-100 absolute bg-red-500 rounded-full -top-1 -right-1"
                                    @click="deleteFile(file.id)"
                                >
                                    <QrIcon icon="trash" class="text-xs m-auto text-white"/>
                                </button>
                            </Thumbnail>
                        </template>
                    </Draggable>
                </div>
            </div>

            <div v-if="uploadFilesFunc">
                <QrButton v-if="!showUploader" @click="showUploader = true">
                    {{ trans('button.upload-more' )}}
                </QrButton>

                <QrFilesUpload
                    v-if="showUploader"
                    :max-files="50"
                    :upload-files="uploadFilesFunc"
                    :helper-text="trans('form.image-types-and-size')"
                />
            </div>

            <div class="sm:flex sm:space-x-4 space-y-2 sm:space-y-0">
                <QrToggle v-model="content.autoplay" :label="trans('homepage.autoplay')"/>
                <QrToggle v-model="content.loop" :label="trans('homepage.loop')"/>
            </div>

            <QrToggle v-model="passwordProtected" :label="trans('homepage.protect-with-password')"/>
            <QrInput
                v-if="!qrCode.homepage.is_password_protected && passwordProtected"
                v-model="password"
                :errors="validationErrors.password"
                :label="trans('homepage.password')"
                trans="homepage.password"
            />
        </QrForm>

        <template #buttons>
            <QrButton
                :text="trans('button.save')"
                trans="button.save"
                @click="update"
                :loading="loading"
                :recently-succeeded="recentlySucceeded"
                :recently-failed="recentlyFailed"
            />
        </template>
    </QrModal>
</template>

<script setup>
import {onBeforeUnmount, onMounted, ref, watch} from "vue";
import QrIcon from "@/components/application-ui/elements/QrIcon.vue";
import QrModal from "@/components/application-ui/dialogs/QrModal.vue";
import QrForm from "@/components/application-ui/content/QrForm.vue";
import QrInput from "@/components/application-ui/form/QrInput.vue";
import { trans } from "@/libs/helper.js";
import QrButton from "@/components/application-ui/elements/QrButton.vue";
import { useQrCodeHomepageStore } from "@/store/qrCodeHomepage.js";
import QrToggle from "@/components/application-ui/form/QrToggle.vue";
import QrFilesUpload from "@/components/application-ui/form/QrFilesUpload.vue";
import createUploader from "@/libs/file-uploader.js";
import { useQrCodesStore } from "@/store/qrCodes.js";
import { storeToRefs } from "pinia";
import QrParagraph from "@/components/application-ui/content/QrParagraph.vue";
import Draggable from "vuedraggable";
import { useStatesStore } from "@/store/states.js";
import DeleteHomePageContent from "@/components/qr-code/homepage/DeleteHomePageContent.vue";
import Thumbnail from "@/components/image/Thumbnail.vue";
import emitter from "@/libs/emitter.js";

const props = defineProps({
    homepageContent: Object,
});

const emit = defineEmits(['close']);

const statesStore = useStatesStore();
const homepageStore = useQrCodeHomepageStore();
const qrCodeStore = useQrCodesStore();

const { qrCode } = storeToRefs(qrCodeStore);
const { loading, recentlyFailed, recentlySucceeded, validationErrors } = storeToRefs(statesStore);
const isDragging = ref(false);
const modalOpen = ref(false);
const menuButtonName = ref('');
const password = ref('');
const passwordProtected = ref(false);
const uploadFilesFunc = ref(null);
const showUploader = ref(false);
const content = ref({
    autoplay: false,
    loop: false,
});

emitter.on('created-gallery', (id) => {
    if (id === props.homepageContent.id) {
        preOpenModal();
    }
});

const getRandomNumber = (min, max) => {
    const random = Math.random() * (max - min) + min
    return random.toFixed(2) + 's';
};

const uploadComplete = ({ status, data }) => {
    if (status === 200) {
        if (props.homepageContent) {
            updateFiles();
            updateFiles(data.data.files);
        }
    }
};

const preOpenModal = async () => {
    menuButtonName.value = props.homepageContent.menu_button_name;
    passwordProtected.value = props.homepageContent.is_password_protected;
    content.value = props.homepageContent.content;
    password.value = props.homepageContent.password
    modalOpen.value = true;
};

const update = async () => {
    await homepageStore.updateContent(props.homepageContent.id, {
        menu_button_name: menuButtonName.value,
        content: content.value,
        is_password_protected: passwordProtected.value,
        password: password.value,
        files: props.homepageContent?.files.map(file => file.id),
    });

    if (Object.keys(validationErrors.value).length === 0) {
        reset();
        modalOpen.value = false
    }
};

const reset = () => {
    content.value = {
        autoplay: false,
        loop: false,
    };
};

const deleteFile = async (fileId) => {
    const data = await homepageStore.deleteFile(props.homepageContent.id, fileId);
    updateFiles(data.files);
};

const updateFiles = (files) => {
    const index = qrCode.value.homepage.contents.findIndex(content => content.id === props.homepageContent.id);
    qrCode.value.homepage.contents[index].files = files;
};

onMounted(() => {
    const { uploadFiles } = createUploader(
        `/api/qr-codes/${qrCode.value.id}/homepage/${props.homepageContent.id}/images`,
        uploadComplete
    );

    uploadFilesFunc.value = uploadFiles
});

watch(modalOpen, () => {
    if (! modalOpen.value) {
        emit('close');
    }

    if (props.homepageContent.files.length) {
        showUploader.value = false;
    }
});
</script>

<style>
.image-shake:nth-child(2n) {
    animation-name: keyframes1;
    animation-iteration-count: infinite;
    transform-origin: 50% 10%;
}

.image-shake:nth-child(2n-1) {
    animation-name: keyframes2;
    animation-iteration-count: infinite;
    animation-direction: alternate;
    transform-origin: 30% 5%;
}

@keyframes keyframes1 {
    0% {
        transform: rotate(-1deg);
        animation-timing-function: ease-in;
    }

    50% {
        transform: rotate(1.5deg);
        animation-timing-function: ease-out;
    }
}

@keyframes keyframes2 {
    0% {
        transform: rotate(1deg);
        animation-timing-function: ease-in;
    }

    50% {
        transform: rotate(-1.5deg);
        animation-timing-function: ease-out;
    }
}
</style>
